import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoney } from '../ui/format'
import { Banner, Empty, ErrorBanner, Panel, Spinner } from '@shop/ui'

type Tab = 'accounts' | 'suppliers' | 'employees'

type Account = { id: string; code: string; nameAr: string; kind: string; normalSide: number }

type TrialLine = { code: string; balance: number }

type Payee = { id: string; name: string }

type PartyBalance = { partyId: string; balance: number }

type Row = { entryDate: string; memo: string | null; debit: number; credit: number; running: number }

type AccountLedger = { opening: number; closing: number; lines: Row[] }

type PartyLedger = { opening: number; balance: number; lines: Row[] }

const KIND_GROUPS: readonly { kind: string; title: string }[] = [
  { kind: 'ASSET', title: 'ما نملكه' },
  { kind: 'LIABILITY', title: 'ما علينا' },
  { kind: 'EQUITY', title: 'رأسُ المال' },
  { kind: 'REVENUE', title: 'الإيرادات' },
  { kind: 'EXPENSE', title: 'المصاريف' },
]

/** المدى بلغةٍ لا تحتاج تقويمًا — والافتراضُ «الكلّ». */
type Range = 'all' | 'month' | 'last'

function iso(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function rangeOf(range: Range): { from: string; to: string } {
  const now = new Date()
  const tomorrow = iso(new Date(now.getTime() + 86_400_000))

  if (range === 'month') {
    return { from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))), to: tomorrow }
  }

  if (range === 'last') {
    return {
      from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))),
      to: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0))),
    }
  }

  return { from: '2000-01-01', to: tomorrow }
}

/**
 * كشفُ الحساب — <b>طلبُ المالك ٢٠٢٦-١٠-٠٨</b>: «بدي صفحة لكشف الحساب للمورد
 * وللموظفين ولكل الحسابات … بسيط، مو شرط محاسب يقدر يشتغل».
 *
 * ⚠ <b>ولا «مدين» ولا «دائن» في هذه الصفحة.</b> الحسابُ يُقرأ «زاد / نقص»،
 *   والمورّدُ «صار علينا / دفعنا»، والموظّفُ «صار له / قبض». والرصيدُ بعد كلّ
 *   حركةٍ في عمودٍ أخير — كدفتر البقالة.
 */
export default function Statement() {
  const { can } = useAuth()
  const [params, setParams] = useSearchParams()

  const tab = (params.get('tab') as Tab | null) ?? 'accounts'
  const selected = tab === 'accounts' ? params.get('code') : params.get('id')
  const range = (params.get('range') as Range | null) ?? 'all'

  const set = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params)

    for (const [key, value] of Object.entries(next)) {
      if (value === null) {
        merged.delete(key)
      } else {
        merged.set(key, value)
      }
    }

    setParams(merged, { replace: true })
  }

  if (!can(Perm.ledgerView)) {
    return (
      <Panel title="كشفُ حساب">
        <Banner tone="warn">هذه الصفحةُ تحتاج صلاحيّةَ «عرضُ الدفاتر».</Banner>
      </Panel>
    )
  }

  return (
    <div className="grid gap-4">
      <Panel title="كشفُ حساب">
        <div className="flex flex-wrap gap-2">
          {([
            ['accounts', 'الحسابات'],
            ['suppliers', 'المورّدون'],
            ['employees', 'الموظّفون'],
          ] as const).map(([id, label]) => (
            <Pill key={id} active={tab === id} onClick={() => set({ tab: id, code: null, id: null })}>
              {label}
            </Pill>
          ))}

          <span className="mx-2 border-s border-line" />

          {([
            ['all', 'من البداية'],
            ['month', 'هذا الشهر'],
            ['last', 'الشهر الماضي'],
          ] as const).map(([id, label]) => (
            <Pill key={id} active={range === id} onClick={() => set({ range: id })}>
              {label}
            </Pill>
          ))}
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        {tab === 'accounts' ? (
          <AccountPicker selected={selected} onPick={(code) => set({ code })} />
        ) : (
          <PartyPicker
            kind={tab === 'suppliers' ? 'SUPPLIER' : 'EMPLOYEE'}
            selected={selected}
            onPick={(id) => set({ id })}
          />
        )}

        {selected === null ? (
          <Panel title="اختر من القائمة">
            <Empty>اضغط على اسمٍ لترى حركاتِه.</Empty>
          </Panel>
        ) : tab === 'accounts' ? (
          <AccountStatement code={selected} range={range} />
        ) : (
          <PartyStatement kind={tab === 'suppliers' ? 'SUPPLIER' : 'EMPLOYEE'} id={selected} range={range} />
        )}
      </div>
    </div>
  )
}

// ── القوائم ─────────────────────────────────────────────────────────────────

function AccountPicker({ selected, onPick }: { selected: string | null; onPick: (code: string) => void }) {
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<Account[]>('/api/v1/accounting/accounts'),
  })

  const { from, to } = rangeOf('all')

  const balances = useQuery({
    queryKey: ['trial-balance', 'all-time'],
    queryFn: () => api<{ lines: TrialLine[] }>(`/api/v1/accounting/trial-balance?from=${from}&to=${to}`),
  })

  if (accounts.isPending) {
    return <Spinner />
  }

  const byCode = new Map((balances.data?.lines ?? []).map((l) => [l.code, l.balance]))

  return (
    <Panel title="الحسابات">
      <ErrorBanner error={accounts.error ?? balances.error} />

      <div className="grid gap-3">
        {KIND_GROUPS.map((group) => (
          <div key={group.kind}>
            <h3 className="mb-1 text-xs font-semibold text-ink-soft">{group.title}</h3>
            <ul className="grid gap-1">
              {(accounts.data ?? []).filter((a) => a.kind === group.kind).map((a) => (
                <li key={a.code}>
                  <PickRow
                    active={selected === a.code}
                    name={a.nameAr}
                    amount={byCode.get(a.code) ?? 0}
                    onClick={() => onPick(a.code)}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Panel>
  )
}

function PartyPicker({
  kind, selected, onPick,
}: {
  kind: 'SUPPLIER' | 'EMPLOYEE'
  selected: string | null
  onPick: (id: string) => void
}) {
  const names = useQuery({
    queryKey: ['payout-options'],
    queryFn: () => api<{ employees: Payee[]; suppliers: Payee[] }>('/api/v1/cash-payouts/options'),
  })

  const balances = useQuery({
    queryKey: ['balances', kind],
    queryFn: () => api<PartyBalance[]>(`/api/v1/accounting/parties?kind=${kind}`),
  })

  if (names.isPending) {
    return <Spinner />
  }

  const people = kind === 'SUPPLIER' ? names.data?.suppliers ?? [] : names.data?.employees ?? []
  const byId = new Map((balances.data ?? []).map((b) => [b.partyId, b.balance]))

  return (
    <Panel title={kind === 'SUPPLIER' ? 'المورّدون' : 'الموظّفون'}>
      <ErrorBanner error={names.error ?? balances.error} />

      {people.length === 0 ? (
        <Empty>لا أحد بعد.</Empty>
      ) : (
        <ul className="grid gap-1">
          {people.map((p) => (
            <li key={p.id}>
              <PickRow
                active={selected === p.id}
                name={p.name}
                amount={byId.get(p.id) ?? 0}
                onClick={() => onPick(p.id)}
              />
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-ink-soft">الرقمُ بجانب الاسم: الباقي علينا له.</p>
    </Panel>
  )
}

// ── الكشوف ──────────────────────────────────────────────────────────────────

function AccountStatement({ code, range }: { code: string; range: Range }) {
  const { from, to } = rangeOf(range)

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<Account[]>('/api/v1/accounting/accounts'),
  })

  const ledger = useQuery({
    queryKey: ['statement', 'account', code, range],
    queryFn: () => api<AccountLedger>(`/api/v1/accounting/accounts/${code}/ledger?from=${from}&to=${to}`),
  })

  const account = accounts.data?.find((a) => a.code === code)

  // ⚠ **«زاد» و«نقص» بحسب طبيعة الحساب**: الصندوقُ يزيد بما يدخله، والذمّةُ
  //   علينا تزيد بما صار علينا — والقارئُ لا يحتاج أن يعرف أيَّهما مدين.
  const grows = (row: Row) => (account?.normalSide ?? 1) > 0 ? row.debit : row.credit
  const shrinks = (row: Row) => (account?.normalSide ?? 1) > 0 ? row.credit : row.debit

  return (
    <StatementTable
      title={account?.nameAr ?? code}
      loading={ledger.isPending}
      error={ledger.error ?? accounts.error}
      opening={ledger.data?.opening ?? 0}
      closing={ledger.data?.closing ?? 0}
      rows={ledger.data?.lines ?? []}
      inLabel="زاد"
      outLabel="نقص"
      balanceLabel="الرصيد"
      inOf={grows}
      outOf={shrinks}
    />
  )
}

function PartyStatement({ kind, id, range }: { kind: 'SUPPLIER' | 'EMPLOYEE'; id: string; range: Range }) {
  const { from, to } = rangeOf(range)

  const names = useQuery({
    queryKey: ['payout-options'],
    queryFn: () => api<{ employees: Payee[]; suppliers: Payee[] }>('/api/v1/cash-payouts/options'),
  })

  const ledger = useQuery({
    queryKey: ['statement', kind, id, range],
    queryFn: () => api<PartyLedger>(`/api/v1/accounting/parties/${kind}/${id}/ledger?from=${from}&to=${to}`),
  })

  const people = kind === 'SUPPLIER' ? names.data?.suppliers ?? [] : names.data?.employees ?? []
  const name = people.find((p) => p.id === id)?.name ?? ''

  return (
    <StatementTable
      title={name}
      loading={ledger.isPending}
      error={ledger.error ?? names.error}
      opening={ledger.data?.opening ?? 0}
      closing={ledger.data?.balance ?? 0}
      rows={ledger.data?.lines ?? []}
      inLabel={kind === 'SUPPLIER' ? 'صار علينا' : 'صار له'}
      outLabel={kind === 'SUPPLIER' ? 'دفعنا له' : 'قبض'}
      balanceLabel={kind === 'SUPPLIER' ? 'الباقي علينا' : 'الباقي له'}
      inOf={(row) => row.credit}
      outOf={(row) => row.debit}
      negativeHint={kind === 'SUPPLIER' ? 'له عندنا (دفعنا أكثر)' : 'سلفة (قبض أكثر)'}
    />
  )
}

function StatementTable({
  title, loading, error, opening, closing, rows, inLabel, outLabel, balanceLabel, inOf, outOf, negativeHint,
}: {
  title: string
  loading: boolean
  error: unknown
  opening: number
  closing: number
  rows: Row[]
  inLabel: string
  outLabel: string
  balanceLabel: string
  inOf: (row: Row) => number
  outOf: (row: Row) => number
  negativeHint?: string
}) {
  const totalIn = rows.reduce((sum, r) => sum + inOf(r), 0)
  const totalOut = rows.reduce((sum, r) => sum + outOf(r), 0)

  return (
    <Panel title={`كشفُ ${title}`}>
      <ErrorBanner error={error} />

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            <Stat label={`مجموع ${inLabel}`} value={formatMoney(totalIn)} />
            <Stat label={`مجموع ${outLabel}`} value={formatMoney(totalOut)} />
            <Stat
              label={closing < 0 && negativeHint ? negativeHint : balanceLabel}
              value={formatMoney(Math.abs(closing))}
              strong
            />
          </div>

          {rows.length === 0 ? (
            <div className="mt-3"><Empty>لا حركاتٍ في هذه الفترة.</Empty></div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[34rem] text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr>
                    <th className="p-2 text-start">التاريخ</th>
                    <th className="p-2 text-start">البيان</th>
                    <th className="p-2 text-end">{inLabel}</th>
                    <th className="p-2 text-end">{outLabel}</th>
                    <th className="p-2 text-end">{balanceLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {opening !== 0 && (
                    <tr className="border-t border-line bg-surface-raised">
                      <td className="p-2" />
                      <td className="p-2 text-ink-soft">رصيدٌ سابق</td>
                      <td className="p-2" />
                      <td className="p-2" />
                      <td className="tabular p-2 text-end font-semibold" dir="ltr">{formatMoney(opening)}</td>
                    </tr>
                  )}
                  {rows.map((row, index) => (
                    <tr key={index} className="border-t border-line">
                      <td className="tabular whitespace-nowrap p-2" dir="ltr">{row.entryDate}</td>
                      <td className="p-2">{row.memo ?? '—'}</td>
                      <td className="tabular p-2 text-end text-ok" dir="ltr">
                        {inOf(row) ? formatMoney(inOf(row)) : ''}
                      </td>
                      <td className="tabular p-2 text-end text-warn" dir="ltr">
                        {outOf(row) ? formatMoney(outOf(row)) : ''}
                      </td>
                      <td className="tabular p-2 text-end font-semibold" dir="ltr">{formatMoney(row.running)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  )
}

// ── أجزاءٌ صغيرة ────────────────────────────────────────────────────────────

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-h-10 rounded-full border px-4 text-sm ${
        active ? 'border-brand bg-brand/10 font-semibold text-brand' : 'border-line bg-surface'
      }`}
    >
      {children}
    </button>
  )
}

function PickRow({
  active, name, amount, onClick,
}: {
  active: boolean
  name: string
  amount: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-baseline gap-2 rounded-lg border px-3 py-2 text-sm ${
        active ? 'border-brand bg-brand/10' : 'border-line hover:bg-surface-raised'
      }`}
    >
      <span className="min-w-0 flex-1 truncate text-start">{name}</span>
      <span className="tabular shrink-0 text-ink-soft" dir="ltr">{formatMoney(amount)}</span>
    </button>
  )
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-lg border border-line p-3">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className={`tabular mt-1 text-xl ${strong ? 'font-bold' : 'font-semibold'}`} dir="ltr">{value}</div>
    </div>
  )
}
